import React from 'react'

import { Box, NumberBadge } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

export type FieldTouched = Partial<Record<keyof LevelData, boolean>> | undefined
export type FieldErrors = Partial<Record<keyof LevelData, string | undefined>>

export interface LevelData {
  id: string | number
  name: string
  description: string
  position?: number
}

interface Props {
  level: LevelData
  position?: number
  errors?: FieldErrors | string
  touched?: FieldTouched
  loading?: boolean
  onShowError?: (field: keyof LevelData, level: LevelData) => boolean
  onChange: (level: LevelData) => void
  onBlur: (field: keyof LevelData) => void
  onSave?: (level: LevelData) => void
  onDiscard?: () => void
}

export const normalizeLevelName = (name: string) => name.toLowerCase().trim()

export const validateLevel = (
  level: Pick<LevelData, 'name' | 'description'>,
  repeatedName = false
): { errors: FieldErrors; hasError: boolean } => {
  const errors: FieldErrors = {}

  if (!normalizeLevelName(level.name)) {
    errors.name = i18n.t('common.validations.empty')
  }

  if (repeatedName) {
    errors.name = i18n.t('competencies_v3.levels.form.repeated_name')
  }

  if (!level.description.trim()) {
    errors.description = i18n.t('common.validations.empty')
  }

  return { errors, hasError: Object.keys(errors).length > 0 }
}

export const LevelForm: React.FC<Props> = ({
  level,
  position,
  errors,
  touched,
  loading = false,
  onShowError,
  onChange,
  onBlur,
  onSave,
  onDiscard,
}) => {
  const showErrorOnTouched = (field: keyof LevelData) => {
    return !!touched?.[field]
  }

  const getError = (field: keyof LevelData) => {
    const show =
      typeof onShowError !== 'undefined' ? onShowError(field, level) : showErrorOnTouched(field)

    if (!show) {
      return undefined
    }

    if (typeof errors === 'string') {
      return errors
    }
    return errors?.[field]
  }

  return (
    <Box gap="s8" flexDirection="row" justifyContent="spaceBetween" width="full">
      {position && <NumberBadge label={position} textColor="grey1000" />}

      <Box gap="s16" width="full">
        <TextField
          label={i18n.t('competencies_v3.levels.form.name_label')}
          value={level.name}
          mandatory
          onChange={(value: string) => onChange({ ...level, name: value })}
          onBlur={() => onBlur('name')}
          error={getError('name')}
          maxLength={256}
          readonly={loading}
        />

        <TextArea
          label={i18n.t('competencies_v3.levels.form.description_label')}
          value={level.description}
          mandatory
          onChange={(value: string) => onChange({ ...level, description: value })}
          onBlur={() => onBlur('description')}
          minRows={4}
          maxHeight="s240"
          error={getError('description')}
          readonly={loading}
        />

        {(onSave || onDiscard) && (
          <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
            {onDiscard && (
              <F0Button
                variant="outline"
                type="button"
                onClick={onDiscard}
                label={i18n.t('competencies_v3.levels.form.discard_button')}
                size="md"
              />
            )}
            {onSave && (
              <F0Button
                type="button"
                onClick={() => onSave(level)}
                label={i18n.t('common.save')}
                size="md"
                disabled={!touched || loading}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
